import type { BranchState } from './branchState'

type Store = Pick<Storage, 'getItem' | 'setItem'>

/** How old a kept queue may be and still be drawn: past this the hall has moved on. */
export const KEPT_STATE_MAX_MS = 6 * 60 * 60 * 1000

/** Where a hall board keeps the last queue it drew, so a reload offline draws it rather than an empty branch. */
export function keptStateKey(branchCode: string, unitCode: string): string {
  return `qms.screen.state.${branchCode}/${unitCode}`
}

/** The calendar day at an instant in a zone, YYYY-MM-DD; the browser's own zone when none is given. */
export function dayIn(zone: string, at: number): string | null {
  try {
    return new Intl.DateTimeFormat('en-CA', { timeZone: zone || undefined, year: 'numeric', month: '2-digit', day: '2-digit' }).format(at)
  } catch {
    return null
  }
}

/** The kept queue, or null when there is none, it is from another day or too old, unreadable, or storage is blocked. */
export function readKeptState(store: Store | undefined, key: string, now: number, zone: string): BranchState | null {
  try {
    const raw = store?.getItem(key)
    if (!raw) return null
    const { at, state: s } = JSON.parse(raw) as { at: number; state: BranchState }
    const ok = typeof at === 'number' && typeof s?.version === 'number' && typeof s.branchName === 'string' &&
      Array.isArray(s.queues) && Array.isArray(s.tickets) && Array.isArray(s.counters)
    if (!ok || now - at > KEPT_STATE_MAX_MS || now < at) return null
    return s.businessDate === dayIn(zone, now) ? s : null
  } catch {
    return null
  }
}

/** Keeps the queue with the moment it was kept; a full or blocked storage keeps nothing. */
export function keepState(store: Store | undefined, key: string, state: BranchState, now: number): void {
  try {
    store?.setItem(key, JSON.stringify({ at: now, state }))
  } catch {
    // Nothing kept: a reload offline draws an empty branch, as before.
  }
}
